import { useState, useEffect } from 'react';
import { Save, Shield, CheckCircle, XCircle, Loader2, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { usePermissions, PageId, RolePermissions } from '../contexts/PermissionsContext';

// All available pages
const ALL_PAGES: { id: PageId; label: string }[] = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'studios', label: 'Studios' },
  { id: 'users', label: 'Users' },
  { id: 'subscriptions', label: 'Subscriptions' },
  { id: 'discounts', label: 'Discounts' },
  { id: 'leads', label: 'Leads' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'guide', label: 'Admin Guide' },
  { id: 'playbook', label: 'Growth Playbook' },
  { id: 'roles', label: 'Roles' },
  { id: 'settings', label: 'Settings' },
];

export default function RolesPage() {
  const { getToken } = useAuth();
  const { permissions: currentPermissions, refetch: refetchPermissions } = usePermissions();
  const [editingPermissions, setEditingPermissions] = useState<RolePermissions | null>(null);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [saveResult, setSaveResult] = useState<{ success: boolean; message: string } | null>(null);
  const [newRoleName, setNewRoleName] = useState('');

  // Initialize editing permissions when current permissions load
  useEffect(() => {
    if (currentPermissions && !editingPermissions) {
      setEditingPermissions(currentPermissions);
    }
  }, [currentPermissions, editingPermissions]);

  const handleSavePermissions = async () => {
    if (!editingPermissions) return;

    setSavingPermissions(true);
    setSaveResult(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/role-permissions`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ permissions: editingPermissions }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save permissions');
      }

      setSaveResult({ success: true, message: 'Role permissions saved successfully' });
      refetchPermissions();
      setTimeout(() => setSaveResult(null), 3000);
    } catch (err) {
      setSaveResult({ success: false, message: err instanceof Error ? err.message : 'Failed to save permissions' });
    } finally {
      setSavingPermissions(false);
    }
  };

  const togglePageForRole = (role: string, pageId: PageId) => {
    if (!editingPermissions) return;

    setEditingPermissions(prev => {
      if (!prev) return prev;
      const rolePerms = prev[role];
      if (!rolePerms) return prev;

      const newPages = rolePerms.pages.includes(pageId)
        ? rolePerms.pages.filter(p => p !== pageId)
        : [...rolePerms.pages, pageId];

      return {
        ...prev,
        [role]: {
          ...rolePerms,
          pages: newPages,
        },
      };
    });
  };

  const updateDefaultPage = (role: string, defaultPage: string) => {
    if (!editingPermissions) return;

    setEditingPermissions(prev => {
      if (!prev) return prev;
      const rolePerms = prev[role];
      if (!rolePerms) return prev;

      return {
        ...prev,
        [role]: {
          ...rolePerms,
          defaultPage,
        },
      };
    });
  };

  const addNewRole = (roleName: string) => {
    if (!editingPermissions || editingPermissions[roleName] || !roleName.trim()) return;

    setEditingPermissions(prev => ({
      ...prev,
      [roleName.toLowerCase()]: {
        pages: [],
        defaultPage: '/',
      },
    }));
    setNewRoleName('');
  };

  const removeRole = (roleName: string) => {
    if (!editingPermissions || roleName === 'admin') return; // Can't remove admin

    setEditingPermissions(prev => {
      if (!prev) return prev;
      const { [roleName]: _, ...rest } = prev;
      return rest;
    });
  };

  if (!editingPermissions) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 text-sage animate-spin" />
          <span className="text-stone-600 dark:text-stone-400">Loading role permissions...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-indigo-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-stone-800 dark:text-stone-100">Role Permissions</h1>
            <p className="text-stone-500 dark:text-stone-400">
              Configure which pages each role can access
            </p>
          </div>
        </div>
        <button
          onClick={handleSavePermissions}
          disabled={savingPermissions}
          className="flex items-center gap-2 px-5 py-2.5 bg-indigo-500 text-white rounded-xl font-medium hover:bg-indigo-600 transition-colors disabled:opacity-50"
        >
          {savingPermissions ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          {savingPermissions ? 'Saving...' : 'Save Permissions'}
        </button>
      </div>

      {/* Save Result */}
      {saveResult && (
        <div className={`flex items-center gap-2 p-3 rounded-xl border ${
          saveResult.success
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
        }`}>
          {saveResult.success ? (
            <CheckCircle className="w-5 h-5 text-green-500" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500" />
          )}
          <p className={`text-sm ${saveResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
            {saveResult.message}
          </p>
        </div>
      )}

      {/* Role Cards */}
      <div className="grid gap-4">
        {Object.entries(editingPermissions).map(([role, rolePerms]) => (
          <div key={role} className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <span className="text-lg font-semibold text-stone-800 dark:text-stone-100 capitalize">{role}</span>
                {role === 'admin' && (
                  <span className="text-xs px-2.5 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full font-medium">
                    Protected
                  </span>
                )}
              </div>
              {role !== 'admin' && (
                <button
                  onClick={() => removeRole(role)}
                  className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 px-3 py-1.5 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove Role
                </button>
              )}
            </div>

            {/* Default Page */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-stone-600 dark:text-stone-400 mb-2">
                Default Page (redirect to on login)
              </label>
              <select
                value={rolePerms.defaultPage}
                onChange={(e) => updateDefaultPage(role, e.target.value)}
                className="w-full max-w-xs px-3 py-2 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              >
                <option value="/">/ (Dashboard)</option>
                <option value="/leads">/leads</option>
                <option value="/playbook">/playbook</option>
                <option value="/studios">/studios</option>
                <option value="/users">/users</option>
                <option value="/roles">/roles</option>
              </select>
            </div>

            {/* Page Checkboxes */}
            <div>
              <label className="block text-sm font-medium text-stone-600 dark:text-stone-400 mb-3">
                Accessible Pages
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {ALL_PAGES.map((page) => (
                  <label
                    key={page.id}
                    className={`flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors ${
                      rolePerms.pages.includes(page.id)
                        ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800'
                        : 'bg-stone-50 dark:bg-stone-800 border-stone-200 dark:border-stone-700 hover:border-stone-300 dark:hover:border-stone-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={rolePerms.pages.includes(page.id)}
                      onChange={() => togglePageForRole(role, page.id)}
                      disabled={role === 'admin' && (page.id === 'settings' || page.id === 'roles')}
                      className="w-4 h-4 text-indigo-500 rounded disabled:opacity-50"
                    />
                    <span className={`text-sm ${
                      rolePerms.pages.includes(page.id)
                        ? 'text-indigo-700 dark:text-indigo-300 font-medium'
                        : 'text-stone-600 dark:text-stone-400'
                    }`}>
                      {page.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add New Role */}
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
        <h3 className="text-lg font-semibold text-stone-800 dark:text-stone-100 mb-4">Add New Role</h3>
        <div className="flex gap-3">
          <input
            type="text"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            placeholder="Enter role name (e.g., sales)"
            className="flex-1 max-w-sm px-4 py-2.5 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl text-stone-700 dark:text-stone-300 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newRoleName.trim()) {
                addNewRole(newRoleName);
              }
            }}
          />
          <button
            onClick={() => addNewRole(newRoleName)}
            disabled={!newRoleName.trim()}
            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-500 text-white rounded-xl font-medium hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-5 h-5" />
            Add Role
          </button>
        </div>
        <p className="text-sm text-stone-500 dark:text-stone-400 mt-3">
          Note: Users with the new role must also have this role set in Clerk (publicMetadata.role).
        </p>
      </div>
    </div>
  );
}
